import { createContext, useCallback, useContext, useMemo } from 'react';
import {
  setEventIdState,
  setEventPersonalDetailsState,
  setEventBusinessDetailsState,
  setEventDetailsState,
  setEventBookingConfirmationState,
  setEventConsentState,
} from 'app/contexts/eventStore/actions';
import {
  EventFlowState,
  EventPersonalDetails,
  EventBusinessDetails,
  EventDetailsInfo,
  EventBookingConfirmation,
  initialEventFlowState,
  useEventStore,
} from 'app/contexts/eventStore/reducer';

interface IEventFlowContext extends EventFlowState {
  setEventId: (eventId: string) => void;
  setPersonalDetails: (personalDetails: EventPersonalDetails) => void;
  setBusinessDetails: (businessDetails: EventBusinessDetails) => void;
  setEventDetails: (eventDetails: EventDetailsInfo) => void;
  setBookingConfirmation: (bookingConfirmation: EventBookingConfirmation) => void;
  setConsent: (hasGivenConsent: boolean) => void;
}

const noop = (): void => undefined;

const EventFlowContext = createContext<IEventFlowContext>({
  ...initialEventFlowState,
  setEventId: noop,
  setPersonalDetails: noop,
  setBusinessDetails: noop,
  setEventDetails: noop,
  setBookingConfirmation: noop,
  setConsent: noop,
});

export const EventFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useEventStore();

  const setEventId = useCallback((eventId: string): void => dispatch(setEventIdState(eventId)), [dispatch]);
  const setPersonalDetails = useCallback(
    (personalDetails: EventPersonalDetails): void => dispatch(setEventPersonalDetailsState(personalDetails)),
    [dispatch],
  );
  const setBusinessDetails = useCallback(
    (businessDetails: EventBusinessDetails): void => dispatch(setEventBusinessDetailsState(businessDetails)),
    [dispatch],
  );
  const setEventDetails = useCallback(
    (eventDetails: EventDetailsInfo): void => dispatch(setEventDetailsState(eventDetails)),
    [dispatch],
  );
  const setBookingConfirmation = useCallback(
    (bookingConfirmation: EventBookingConfirmation): void =>
      dispatch(setEventBookingConfirmationState(bookingConfirmation)),
    [dispatch],
  );
  const setConsent = useCallback(
    (hasGivenConsent: boolean): void => dispatch(setEventConsentState(hasGivenConsent)),
    [dispatch],
  );

  const providerValue = useMemo(
    () => ({
      ...state,
      setEventId,
      setPersonalDetails,
      setBusinessDetails,
      setEventDetails,
      setBookingConfirmation,
      setConsent,
    }),
    [state, setEventId, setPersonalDetails, setBusinessDetails, setEventDetails, setBookingConfirmation, setConsent],
  );

  return <EventFlowContext.Provider value={providerValue}>{children}</EventFlowContext.Provider>;
};

export const useEventFlow = (): IEventFlowContext => useContext(EventFlowContext);

export default EventFlowContext;
